import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ButtonGroup,
  useTheme,
  useMediaQuery,
  Alert,
  Autocomplete
} from '@mui/material';

import Sidebar from '../components/Sidebar.tsx';
import { useAuth } from '../context/AuthContext.tsx';

const API_BASE = process.env.REACT_APP_API_URL + '/api';


interface WorkingDay {
  date: Date;
  minDays: number;
  maxDays: number;
  notes: string;
}

type ShiftPreference = 'vroege' | 'late' | 'beide' | 'niet-werken' | null;

interface DayPreference {
  date: string;
  preference: ShiftPreference;
}

interface DraftSnapshot {
  minDays: string;
  maxDays: string;
  notes: string;
  dayPreferences: DayPreference[];
  submitStatus: 'unknown' | 'submitted' | 'open';
  isDirty: boolean;
}

const VerplegingWensenlijsten: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  
  // Bepaal welke maand getoond moet worden
  const getInitialPlanningMonth = () => {
    const now = new Date();
    // Toon de maand die 2 maanden vooruit ligt
    return new Date(now.getFullYear(), now.getMonth() + 2, 1);
  };
  
  const [currentMonth, setCurrentMonth] = useState(getInitialPlanningMonth());
  const [minDays, setMinDays] = useState('');
  const [maxDays, setMaxDays] = useState('');
  const [notes, setNotes] = useState('');
  const [dayPreferences, setDayPreferences] = useState<DayPreference[]>([]);
  const [submitStatus, setSubmitStatus] = useState<'unknown' | 'submitted' | 'open'>('unknown');
  const [wishlistKey, setWishlistKey] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDialogDate, setSelectedDialogDate] = useState<Date | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedNurse, setSelectedNurse] = useState<number | "">("");
  const [isViewingOtherNurse, setIsViewingOtherNurse] = useState(false);
  const [nurses, setNurses] = useState<{ id: number; name: string }[]>([]);
  const [wishlistLoading, setWishlistLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const latestRequestKeyRef = useRef('');
  const draftCacheRef = useRef<Record<string, DraftSnapshot>>({});



  const buildWishlistKey = (nurseId: number, year: number, month: number) =>
    `${nurseId}-${year}-${month}`;

  const activeNurseId =
    user?.role === 'admin' && selectedNurse ? Number(selectedNurse) : user?.id;
  const activeKey = activeNurseId
    ? buildWishlistKey(
        activeNurseId,
        currentMonth.getFullYear(),
        currentMonth.getMonth() + 1
      )
    : '';
  const isCurrentData = wishlistKey === activeKey;
  const effectiveSubmitStatus = isCurrentData ? submitStatus : 'unknown';
  const isSubmitted = effectiveSubmitStatus === 'submitted';
  const isStatusUnknown = effectiveSubmitStatus === 'unknown';
  const displayDayPreferences = isCurrentData ? dayPreferences : [];
  const displayMinDays = isCurrentData ? minDays : '';
  const displayMaxDays = isCurrentData ? maxDays : '';
  const displayNotes = isCurrentData ? notes : '';
  const isLocked = (() => {
    if (user?.role === 'admin') return false;
    return effectiveSubmitStatus === 'submitted';
  })();

  const buildDraftSnapshot = (
    overrides: Partial<DraftSnapshot> = {}
  ): DraftSnapshot => ({
    minDays,
    maxDays,
    notes,
    dayPreferences: dayPreferences.map((p) => ({ ...p })),
    submitStatus,
    isDirty,
    ...overrides
  });

  const hydrateFromDraft = (key: string, draft: DraftSnapshot) => {
    setWishlistKey(key);
    setSubmitStatus(draft.submitStatus);
    setMinDays(draft.minDays);
    setMaxDays(draft.maxDays);
    setNotes(draft.notes);
    setDayPreferences(draft.dayPreferences.map((p) => ({ ...p })));
    setIsDirty(draft.isDirty);
  };

  const cacheCurrentDraft = (dirtyOverride?: boolean) => {
    if (!activeKey) return;
    draftCacheRef.current[activeKey] = buildDraftSnapshot(
      typeof dirtyOverride === 'boolean' ? { isDirty: dirtyOverride } : {}
    );
  };


    // Ophalen wishlist uit database
  const fetchWishlistData = async (nurseId: number, targetMonth: Date) => {
    let requestKey = '';
    try {
      const year = targetMonth.getFullYear();
      const month = targetMonth.getMonth() + 1;
      requestKey = buildWishlistKey(nurseId, year, month);
      latestRequestKeyRef.current = requestKey;
      setWishlistLoading(true);

      const token = localStorage.getItem("auth_token");

      const res = await fetch(
        `${API_BASE}/wishlist/${nurseId}/${year}/${month}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!res.ok) throw new Error("Wishlist ophalen mislukt");
      const data = await res.json();

      if (latestRequestKeyRef.current !== requestKey) {
        return;
      }

      const cached = draftCacheRef.current[requestKey];
      if (cached?.isDirty) {
        hydrateFromDraft(requestKey, cached);
        return;
      }

      const serverSnapshot: DraftSnapshot = {
        minDays: data.minDays !== null ? String(data.minDays) : "",
        maxDays: data.maxDays !== null ? String(data.maxDays) : "",
        notes: data.notes ?? "",
        dayPreferences: Array.isArray(data.preferences)
          ? data.preferences.map((p: any) => ({
              date: p.date,
              preference: p.preference
            }))
          : [],
        submitStatus: Number(data.submitted) === 1 ? 'submitted' : 'open',
        isDirty: false
      };

      draftCacheRef.current[requestKey] = serverSnapshot;
      hydrateFromDraft(requestKey, serverSnapshot);
    } catch (err) {
      console.error(err);
      if (requestKey && latestRequestKeyRef.current === requestKey) {
        const cached = draftCacheRef.current[requestKey];
        if (cached) {
          hydrateFromDraft(requestKey, cached);
        } else {
          setWishlistKey('');
          setIsDirty(false);
        }
      }
    } finally {
      if (requestKey && latestRequestKeyRef.current === requestKey) {
        setWishlistLoading(false);
      }
    }
  };


  useEffect(() => {
    document.title = 'Verpleging Wensenlijsten - Thuisverpleging Portaal';
  }, []);

  useEffect(() => {
    if (!activeKey) return;
    const cached = draftCacheRef.current[activeKey];
    if (!cached) return;
    hydrateFromDraft(activeKey, cached);
  }, [activeKey]);

    // Automatisch opnieuw inladen zodra maand wijzigt
    useEffect(() => {
    if (activeNurseId) {
      fetchWishlistData(activeNurseId, currentMonth);
    }
  }, [currentMonth, selectedNurse, activeNurseId]);


  // Ophalen echte verpleegkundigen uit DB
  useEffect(() => {
    const fetchNurses = async () => {
      try {
        const token = localStorage.getItem("auth_token");

        const res = await fetch(`${API_BASE}/nurses`, {
          headers: {
            "Authorization": `Bearer ${token}`
          }
        });

        if (!res.ok) throw new Error("Unauthorized");

        const data = await res.json();

        setNurses(
          Array.isArray(data)
            ? data.map((n) => ({
                id: n.id,
                name: n.name
              }))
            : []
        );

      } catch (err) {
        console.error("Nurses ophalen mislukt", err);
      }
    };

    if (user?.role === 'admin') {
      fetchNurses();
    }
  }, [user]);



  const monthNames = [
    'januari', 'februari', 'maart', 'april', 'mei', 'juni',
    'juli', 'augustus', 'september', 'oktober', 'november', 'december'
  ];

  const dayNames = ['ma.', 'di.', 'wo.', 'do.', 'vr.', 'za.', 'zo.'];

  const handleUnlockWishlist = async () => {
  if (!activeNurseId) return;

  try {
    const token = localStorage.getItem("auth_token");
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;

    const res = await fetch(
      `${API_BASE}/wishlist/${activeNurseId}/${year}/${month}/unlock`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    if (!res.ok) {
      const data = await res.json().catch(() => null);
      throw new Error(data?.message || 'Vrijstellen mislukt');
    }

    setSubmitStatus('open');
    fetchWishlistData(activeNurseId, currentMonth);

  } catch (err: any) {
    alert(err.message || 'Vrijstellen mislukt');
  }
};


  const handleNurseChange = (event: any) => {
    setSelectedNurse(event.target.value);
  };

  const handleDayClick = (date: Date) => {
    if (isLocked) return;

    setSelectedDialogDate(date);
    setDialogOpen(true);
  };


  const handleShiftPreferenceChange = (preference: ShiftPreference) => {
  if (!selectedDialogDate) return;
  if (isLocked) return;
  if (isSubmitted && user?.role !== 'admin') return;


    const dateString = normalizeDate(selectedDialogDate);
    setIsDirty(true);

    setDayPreferences((prev) => {
      const existingIndex = prev.findIndex(p => p.date === dateString);

      if (existingIndex >= 0) {
        return prev.map((p, idx) =>
          idx === existingIndex ? { date: dateString, preference } : p
        );
      }

      return [...prev, { date: dateString, preference }];
    });

    setDialogOpen(false);
  };


    const normalizeDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };


  const getDayPreference = (date: Date): ShiftPreference => {
    const dateString = normalizeDate(date);
    const pref = displayDayPreferences.find(p => p.date === dateString);
    return pref ? pref.preference : null;
  };


  const getPreferenceColor = (preference: ShiftPreference): string => {
    if (preference === null) return '#4CAF50'; // → Groen voor vroege en late
    switch (preference) {
      case 'vroege': return '#2196F3';
      case 'late': return '#FF9800';
      case 'beide': return '#4CAF50';
      case 'niet-werken': return '#F44336';
      default: return '#4CAF50';
    }
  };

  const getPreferenceText = (preference: ShiftPreference): string => {
    if (preference === null) return 'Vroege en Late';
    switch (preference) {
      case 'vroege': return 'Vroege';
      case 'late': return 'Late';
      case 'beide': return 'Vroege en Late'; // uniform maken zoals jij vroeg
      case 'niet-werken': return 'Niet werken';
      default: return 'Vroege en Late';
    }
  };


  const saveDraftIfNeeded = async (): Promise<boolean> => {
    if (!activeNurseId || !isDirty || isLocked || isStatusUnknown) {
      return true;
    }

    cacheCurrentDraft(true);

    try {
      setIsDraftSaving(true);
      const token = localStorage.getItem("auth_token");
      if (!token) return false;

      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      const body = {
        minDays: minDays === '' ? null : Number(minDays),
        maxDays: maxDays === '' ? null : Number(maxDays),
        notes,
        submitted: false,
        preferences: dayPreferences
          .filter((p) => p.preference !== null)
          .map((p) => ({
            date: p.date,
            preference: p.preference
          }))
      };

      const res = await fetch(
        `${API_BASE}/wishlist/${activeNurseId}/${year}/${month}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(body)
        }
      );

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || "Concept opslaan mislukt");
      }

      setSubmitStatus('open');
      setWishlistKey(buildWishlistKey(activeNurseId, year, month));
      setIsDirty(false);
      draftCacheRef.current[buildWishlistKey(activeNurseId, year, month)] = buildDraftSnapshot({
        submitStatus: 'open',
        isDirty: false
      });
      return true;
    } catch (err) {
      console.error("Fout bij concept opslaan:", err);
      return false;
    } finally {
      setIsDraftSaving(false);
    }
  };

  const changeMonth = (nextMonth: Date) => {
    if (isSubmitting) return;
    cacheCurrentDraft();
    void saveDraftIfNeeded();
    setCurrentMonth(nextMonth);
  };

  const navigateToToday = () => {
    // Ga naar de planning maand (2 maanden vooruit)
    changeMonth(getInitialPlanningMonth());
  };

const navigateToPreviousMonth = () => {
  const previous = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() - 1,
    1
  );
  changeMonth(previous);
};




const navigateToNextMonth = () => {
  const next = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
  changeMonth(next);
};



  const handleSubmitWishlist = () => {
    if (isStatusUnknown) {
      return;
    }
    if (isSubmitted && user?.role !== 'admin') {
      return;
    }
    setConfirmDialogOpen(true);
  };

const confirmSubmitWishlist = async () => {
  if (!confirmDialogOpen || isSubmitting) return;
  setIsSubmitting(true);

  try {
    if (isStatusUnknown) {
      return;
    }

    if (isSubmitted && user?.role !== "admin") {
      return;
    }

    const nurseId = activeNurseId;
    if (!nurseId) {
      return;
    }

    const token = localStorage.getItem("auth_token");
    if (!token) {
      return;
    }

    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;

    const body = {
      minDays: minDays === '' ? null : Number(minDays),
      maxDays: maxDays === '' ? null : Number(maxDays),
      notes,
      submitted: true,
      preferences: dayPreferences
        .filter(p => p.preference !== null)
        .map(p => ({
          date: p.date,
          preference: p.preference
        }))
    };

    const res = await fetch(
      `${API_BASE}/wishlist/${nurseId}/${year}/${month}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      }
    );

    if (!res.ok) {
      if (res.status === 409) {
        setSubmitStatus('submitted');
        setIsDirty(false);
        setConfirmDialogOpen(false);
        cacheCurrentDraft(false);
        return;
      }

      const data = await res.json().catch(() => null);
      throw new Error(data?.message || "Opslaan mislukt");
    }

    setSubmitStatus('submitted');
    setIsDirty(false);
    setDialogOpen(false);
    setConfirmDialogOpen(false);
    setSelectedDialogDate(null);

    setWishlistKey(
      buildWishlistKey(
        nurseId,
        year,
        month
      )
    );

    cacheCurrentDraft(false);
    await fetchWishlistData(nurseId, currentMonth);

  } catch (err: any) {
    console.error("Fout bij indienen:", err);

    if (err.message?.includes("definitief")) {
      setSubmitStatus('submitted');
      setConfirmDialogOpen(false);
      return;
    }
  } finally {
    setIsSubmitting(false);
  }
};


  const isCurrentPlanningMonth = () => {
    const planningMonth = getInitialPlanningMonth();
    return currentMonth.getMonth() === planningMonth.getMonth() && 
           currentMonth.getFullYear() === planningMonth.getFullYear();
  };

  const renderCalendarHeader = () => {
    const month = currentMonth.getMonth();
    const year = currentMonth.getFullYear();
    const now = new Date();
    const monthsAhead = (year - now.getFullYear()) * 12 + (month - now.getMonth());
    const planningMonth = new Date(now.getFullYear(), now.getMonth() + 2, 1);
    const isPlanningMonth = currentMonth.getMonth() === planningMonth.getMonth() && 
                           currentMonth.getFullYear() === planningMonth.getFullYear();
    
    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button 
          variant="outlined"
          onClick={navigateToPreviousMonth}
          disabled={isSubmitting}
        >
          ← Vorige maand
        </Button>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ color: '#4aa8b4' }}>
            {monthNames[month]} {year}
          </Typography>
          {isPlanningMonth && !isLocked && (
            <Typography 
              variant="caption" 
              sx={{ color: 'error.main' }}
            >
              Enkel invulbaar tot einde huidige maand
            </Typography>
          )}
        </Box>
        <Button 
          variant="outlined"
          onClick={navigateToNextMonth}
          disabled={isSubmitting}
        >
          Volgende maand →
        </Button>
      </Box>
    );
  };

  const renderCalendarGrid = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = (firstDay.getDay() + 6) % 7; // Adjust for Monday start

    const days = [];
    
    // Add day headers
    dayNames.forEach((day, index) => {
      days.push(
        <Box key={`header-${index}`} sx={{ 
          p: 1, 
          textAlign: 'center', 
          fontWeight: 'bold',
          backgroundColor: '#f5f5f5',
          border: '1px solid #e0e0e0'
        }}>
          {day}
        </Box>
      );
    });

    // Add empty cells for days before month starts
    for (let i = 0; i < startingDay; i++) {
      const prevMonthDay = new Date(year, month, -startingDay + i + 1).getDate();
      days.push(
        <Box key={`empty-${i}`} sx={{ 
          p: 2, 
          minHeight: 60,
          backgroundColor: '#f9f9f9',
          border: '1px solid #e0e0e0',
          color: '#ccc'
        }}>
          {prevMonthDay}
        </Box>
      );
    }

    // Add days of current month
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(year, month, day);
      const isToday = currentDate.toDateString() === new Date().toDateString();
      const isSelected = selectedDate && currentDate.toDateString() === selectedDate.toDateString();
      const preference = getDayPreference(currentDate);
      
      const isEditable = !isLocked;

      
      days.push(
        <Box 
          key={day}
          onClick={() => handleDayClick(currentDate)}
          sx={{
            p: 1,
            minHeight: 80,
            border: '1px solid #e0e0e0',
            cursor: isEditable ? 'pointer' : 'not-allowed',
            backgroundColor: isToday
              ? '#e3f2fd'
              : (user?.role === "admin"
                  ? (isStatusUnknown ? '#f5f5f5' : 'white')
                  : (isEditable ? 'white' : '#f5f5f5')),
            opacity: isEditable ? 1 : 0.6,
            '&:hover': {
              backgroundColor: (!isStatusUnknown && (user?.role === "admin" || isEditable)) ? '#f0f0f0' : '#f5f5f5'
            },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: isToday ? 'bold' : 'normal' }}>
            {day}
          </Typography>
          <Box
            sx={{
              width: '100%',
              height: 30,
              backgroundColor: isStatusUnknown ? '#9e9e9e' : getPreferenceColor(preference),
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '10px',
              fontWeight: 'bold',
              cursor: isEditable ? 'pointer' : 'not-allowed',
              '&:hover': {
                opacity: isEditable ? 0.8 : 1
              }
            }}
          >
            {isStatusUnknown
              ? 'Laden...'
              : (preference 
                  ? getPreferenceText(preference) 
                  : (user?.role === "admin" 
                      ? 'Vroege en Late' 
                      : (!isEditable ? 'Niet bewerkbaar' : 'Vroege en Late')
                    )
                )
            }
          </Box>
        </Box>
      );
    }

    return (
      
      <Grid container sx={{ border: '1px solid #e0e0e0' }}>
        {days.map((day, index) => (
          <Grid item xs={12/7} key={index} sx={{ minHeight: 60 }}>
            {day}
          </Grid>
        ))}
      </Grid>
    );
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar open={true} onClose={() => {}} />
      
      <Box component="main" sx={{ 
        flexGrow: 1, 
        p: { xs: 1, sm: 2, md: 3 },
        mt: { xs: '60px', md: 0 },
        ml: { xs: 0, md: '70px' }
      }}>
        <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
          {/* Header */}
          <Typography 
            variant={isMobile ? 'h5' : 'h4'} 
            component="h1" 
            gutterBottom 
            sx={{ 
              color: '#4aa8b4', 
              mb: 1,
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
            }}
          >
            Verpleging Wensenlijsten
          </Typography>
          <Typography 
            variant={isMobile ? 'body2' : 'body1'} 
            color="text.secondary" 
            sx={{ 
              mb: 3,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
          </Typography>

          {/* Admin: Nurse Selection Dropdown */}
          {user?.role === 'admin' && (
            <Box sx={{ mb: 3 }}>
              <Autocomplete
                fullWidth
                disablePortal
                options={nurses}
                getOptionLabel={(option) => option.name}
                value={nurses.find((n) => n.id === selectedNurse) || null}
                onChange={(_, value) => {
                const nurseId = value ? value.id : "";

                // 🔒 eerst eventuele draft veilig opslaan
                cacheCurrentDraft();
                void saveDraftIfNeeded();

                setSelectedNurse(nurseId);

                if (nurseId && user?.role === "admin") {
                  setIsViewingOtherNurse(true);
                } else {
                  setIsViewingOtherNurse(false);
                }
              }}
                renderInput={(params) => (
                  <TextField {...params} label="Verpleegkundige zoeken..." />
                )}
                sx={{ mb: 2 }}
              />
              {isViewingOtherNurse && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Je bekijkt de wensen van {
                    nurses.find(n => n.id === selectedNurse)?.name || ''
                  }.
                  Deze zijn {isStatusUnknown ? 'aan het laden' : (isSubmitted ? 'al ingediend' : 'nog niet ingediend')}.
                </Alert>
              )}
            </Box>
          )}

          

          {/* Calendar Navigation */}
          <Box sx={{ 
            mb: 2,
            display: 'flex',
            justifyContent: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 1 }
          }}>
          </Box>

          {/* Calendar */}
          <Paper sx={{ 
            p: { xs: 1, sm: 2, md: 3 }, 
            mb: 4,
            overflow: { xs: 'auto', md: 'visible' }
          }}>
            {renderCalendarHeader()}
            {renderCalendarGrid()}
          </Paper>

          {/* Working Days Input */}
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            {isSubmitted && user?.role !== 'admin' && (
              <Box sx={{ mb: 3 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#4CAF50', 
                    mb: 1, 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  ✅ Wensenlijst Ingediend
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Je wensenlijst voor {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()} is succesvol ingediend en kan niet meer worden aangepast.
                </Typography>
              </Box>
            )}
            {isSubmitted && user?.role !== 'admin' && (
              <Alert severity="success" sx={{ mb: 3 }}>
                Deze maand is definitief ingediend. Je kan niks meer aanpassen.
              </Alert>
            )}
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Min. dagen werken*"
                  placeholder="Min. dagen"
                  value={displayMinDays}
                  onChange={(e) => {
                    setMinDays(e.target.value);
                    setIsDirty(true);
                  }}
                  size={isMobile ? 'small' : 'medium'}
                  type="number"
                  InputProps={{ inputProps: { min: 0, max: 31 } }}
                  disabled={isLocked || isViewingOtherNurse}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Max. dagen werken*"
                  placeholder="Max. dagen"
                  value={displayMaxDays}
                  onChange={(e) => {
                    setMaxDays(e.target.value);
                    setIsDirty(true);
                  }}
                  type="number"
                  size={isMobile ? 'small' : 'medium'}
                  InputProps={{ inputProps: { min: 0, max: 31 } }}
                  disabled={isLocked || isViewingOtherNurse}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Opmerkingen"
                  placeholder="Voeg hier je opmerkingen toe..."
                  value={displayNotes}
                  onChange={(e) => {
                    setNotes(e.target.value);
                    setIsDirty(true);
                  }}
                  multiline
                  rows={isMobile ? 3 : 4}
                  size={isMobile ? 'small' : 'medium'}
                  disabled={isLocked || isViewingOtherNurse}
                />
              </Grid>
              <Grid item xs={12}>
  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>

    {/* Admin: vrijstellen / override */}
    {user?.role === 'admin' && (
      <>
        <Button
          variant="contained"
          onClick={handleUnlockWishlist}
          disabled={!isSubmitted || !activeNurseId}
          sx={{
            bgcolor: isSubmitted ? '#1976d2' : '#c3c3c3',
            cursor: isSubmitted ? 'pointer' : 'not-allowed',
            '&:hover': {
              bgcolor: isSubmitted ? '#1565c0' : '#c3c3c3'
            },
            px: 4,
            py: 1.5,
            fontWeight: 'bold'
          }}
        >
          Vrijstellen
        </Button>

        <Button
          variant="contained"
          onClick={handleSubmitWishlist}
          disabled={!selectedNurse}
          sx={{
            bgcolor: '#4aa8b4',
            '&:hover': { bgcolor: '#3a8a94' },
            px: 4,
            py: 1.5,
            fontWeight: 'bold'
          }}
        >
          Override
        </Button>
      </>
    )}

    {/* Verpleegkundige: normaal indienen */}
    {user?.role !== 'admin' && (
      <Button
        variant="contained"
        onClick={handleSubmitWishlist}
        disabled={isSubmitted || isLocked || isStatusUnknown}
        sx={{
          bgcolor: !isSubmitted ? '#4aa8b4' : '#c3c3c3',
          '&:hover': {
            bgcolor: !isSubmitted ? '#3a8a94' : '#c3c3c3'
          },
          px: 4,
          py: 1.5,
          fontWeight: 'bold'
        }}
      >
        Wensenlijst indienen
      </Button>
    )}

  </Box>
</Grid>
            </Grid>
          </Paper>

          {/* Shift Preference Dialog */}
          <Dialog 
            open={dialogOpen} 
            onClose={() => setDialogOpen(false)} 
            maxWidth="sm" 
            fullWidth
            PaperProps={{
              sx: {
                margin: { xs: 1, sm: 2 },
                width: { xs: 'calc(100% - 16px)', sm: 'auto' }
              }
            }}
          >
            <DialogTitle sx={{ 
              textAlign: 'center', 
              color: '#4aa8b4',
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              p: { xs: 2, sm: 3 }
            }}>
              Selecteer shift voorkeur
              {selectedDialogDate && (
                <Typography variant="body2" color="text.secondary">
                  {selectedDialogDate.toLocaleDateString('nl-NL', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </Typography>
              )}
            </DialogTitle>
            <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: { xs: 1.5, sm: 2 }, 
                mt: 2 
              }}>
                <Button
                  variant="contained"
                  onClick={() => handleShiftPreferenceChange('vroege')}
                  sx={{
                    backgroundColor: '#2196F3',
                    '&:hover': { backgroundColor: '#1976D2' },
                    py: { xs: 1.5, sm: 2 },
                    fontSize: { xs: '14px', sm: '16px' }
                  }}
                >
                  Alleen Vroege Shift
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleShiftPreferenceChange('late')}
                  sx={{
                    backgroundColor: '#FF9800',
                    '&:hover': { backgroundColor: '#F57C00' },
                    py: { xs: 1.5, sm: 2 },
                    fontSize: { xs: '14px', sm: '16px' }
                  }}
                >
                  Alleen Late Shift
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleShiftPreferenceChange('beide')}
                  sx={{
                    backgroundColor: '#4CAF50',
                    '&:hover': { backgroundColor: '#388E3C' },
                    py: { xs: 1.5, sm: 2 },
                    fontSize: { xs: '14px', sm: '16px' }
                  }}
                >
                  Vroege en Late
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleShiftPreferenceChange('niet-werken')}
                  sx={{
                    backgroundColor: '#F44336',
                    '&:hover': { backgroundColor: '#D32F2F' },
                    py: { xs: 1.5, sm: 2 },
                    fontSize: { xs: '14px', sm: '16px' }
                  }}
                >
                  Niet Werken
                </Button>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
              <Button 
                onClick={() => setDialogOpen(false)} 
                color="primary"
                size={isMobile ? 'small' : 'medium'}
              >
                Annuleren
              </Button>
            </DialogActions>
          </Dialog>

          {/* Confirmation Dialog */}
          <Dialog 
            open={confirmDialogOpen} 
            onClose={() => setConfirmDialogOpen(false)} 
            maxWidth="sm" 
            fullWidth
            PaperProps={{
              sx: {
                margin: { xs: 1, sm: 2 },
                width: { xs: 'calc(100% - 16px)', sm: 'auto' }
              }
            }}
          >
            <DialogTitle sx={{ 
              textAlign: 'center', 
              color: '#4aa8b4',
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              p: { xs: 2, sm: 3 }
            }}>
              ⚠️ Wensenlijst Definitief Versturen
            </DialogTitle>
            <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>
                {user?.role === 'admin' && selectedNurse
                  ? `Je staat op het punt de wensenlijst van ${
                      nurses.find(n => n.id === selectedNurse)?.name || 'Onbekend'
                    } te overriden.`
                  : 'Weet je zeker dat je je wensenlijst wilt versturen?'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                <strong>Let op:</strong> Na het versturen kan je wensenlijst niet meer worden aangepast. 
                Controleer goed of alle gegevens correct zijn:
              </Typography>
              <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2"><strong>Min. dagen:</strong> {displayMinDays || 'Niet ingevuld'}</Typography>
                <Typography variant="body2"><strong>Max. dagen:</strong> {displayMaxDays || 'Niet ingevuld'}</Typography>
                <Typography variant="body2"><strong>Shift voorkeuren:</strong> {displayDayPreferences.length} dagen ingesteld</Typography>
                <Typography variant="body2"><strong>Opmerkingen:</strong> {displayNotes || 'Geen opmerkingen'}</Typography>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: { xs: 2, sm: 3 }, justifyContent: 'center', gap: 2 }}>
              <Button 
                onClick={() => setConfirmDialogOpen(false)} 
                variant="outlined"
                size={isMobile ? 'small' : 'medium'}
              >
                Annuleren
              </Button>
              <Button
                onClick={confirmSubmitWishlist}
                disabled={isSubmitting}
                variant="contained"
                color="primary"
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  bgcolor: '#4aa8b4',
                  '&:hover': { bgcolor: '#3a8a94' }
                }}
              >
                Ja, Definitief Versturen
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Box>
    </Box>
  );
};

export default VerplegingWensenlijsten;
